import { render } from 'solid-js/web';
import { MetaProvider } from '@solidjs/meta';
import { AppRouter } from './app/router';
import { initSession } from './features/auth/store/session.store';
import { initTheme } from './theme';
import { cleanupDeprecatedStorage } from './shared/lib/cleanup';
import './index.css';

const CHUNK_RELOAD_GUARD_KEY = 'khoai:chunk-reload-guard';
const CHUNK_RELOAD_GUARD_WINDOW_MS = 60_000;

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;

  return /Failed to fetch dynamically imported module|dynamically imported module|Loading chunk|Importing a module script failed|MIME type of "text\/html"/i.test(
    err.message
  );
}

function shouldAutoReloadForChunkError(): boolean {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    if (!raw) return true;

    const parsed = JSON.parse(raw) as {
      path?: string;
      at?: number;
    };

    if (parsed.path !== window.location.pathname) return true;
    if (typeof parsed.at !== 'number') return true;

    return Date.now() - parsed.at > CHUNK_RELOAD_GUARD_WINDOW_MS;
  } catch {
    return true;
  }
}

function markChunkReloadAttempt(): void {
  try {
    sessionStorage.setItem(
      CHUNK_RELOAD_GUARD_KEY,
      JSON.stringify({
        path: window.location.pathname,
        at: Date.now(),
      })
    );
  } catch {
    // Ignore storage failures in strict privacy modes.
  }
}

function setupChunkRecovery(): void {
  const tryRecover = (reason: string, err?: unknown): void => {
    if (err && !isChunkLoadError(err)) return;
    if (!shouldAutoReloadForChunkError()) return;

    markChunkReloadAttempt();
    console.warn('[chunk-recovery] Reloading after chunk load failure', {
      reason,
      path: window.location.pathname,
    });
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', (event: Event) => {
    const customEvent = event as CustomEvent<unknown>;
    customEvent.preventDefault();
    tryRecover('vite:preloadError', customEvent.detail);
  });

  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      tryRecover('unhandledrejection', event.reason);
    }
  );

  // Clear the guard after a stable period so future deploys can recover again.
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    } catch {
      // Ignore storage failures.
    }
  }, 30_000);
}

function isPublicMarketingPath(pathname: string): boolean {
  return (
    pathname === '/welcome' ||
    pathname === '/pricing' ||
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password' ||
    pathname === '/onboarding' ||
    pathname === '/privacy' ||
    pathname === '/terms' ||
    pathname === '/billing-terms' ||
    pathname.startsWith('/features/') ||
    pathname.startsWith('/blogs') ||
    pathname === '/vi/welcome' ||
    pathname === '/vi/pricing' ||
    pathname === '/vi/privacy' ||
    pathname === '/vi/terms' ||
    pathname === '/vi/billing-terms' ||
    pathname.startsWith('/vi/features/') ||
    pathname.startsWith('/vi/blogs')
  );
}

function bootstrapSession(pathname: string): void {
  if (!isPublicMarketingPath(pathname)) {
    initSession();
    return;
  }

  const deferredInit = () => {
    // Fire and forget: public pages should not block first content on auth refresh.
    initSession();
  };

  const requestIdleCallbackFn = (
    globalThis as {
      requestIdleCallback?: (
        cb: IdleRequestCallback,
        opts?: IdleRequestOptions
      ) => number;
    }
  ).requestIdleCallback;

  if (typeof requestIdleCallbackFn === 'function') {
    requestIdleCallbackFn(() => deferredInit(), { timeout: 2000 });
    return;
  }

  globalThis.setTimeout(deferredInit, 1500);
}

// Clean up deprecated localStorage items from previous versions
cleanupDeprecatedStorage();

// Initialize theme system first
initTheme();

// Recover from stale hashed chunk requests after deployments.
setupChunkRecovery();

// Get root element
const root = document.getElementById('root');

if (!root) {
  throw new Error('Root element not found');
}

// Clear any prerendered HTML before mounting the client app.
// Solid's `render()` appends to the container instead of replacing,
// so without this the prerendered markup would remain in the DOM
// alongside the client-rendered tree (causing duplicated content).
root.innerHTML = '';

// Render app immediately (don't wait for session init)
// ProtectedRoute will handle loading state
render(
  () => (
    <MetaProvider>
      <AppRouter />
    </MetaProvider>
  ),
  root
);

// Keep auth bootstrap immediate for app routes, but defer it on public
// marketing/auth routes to avoid extending the critical request chain.
bootstrapSession(window.location.pathname);
